import { useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { canManageRfqScopes } from "@/features/rfq/api/scopes";
import {
  useDefaultDeclarationQuestions,
  useDeleteDeclarationQuestion,
  useSaveDeclarationQuestion,
} from "@/features/rfq/api/proceedings";

/**
 * Biblioteka domyślnych oświadczeń oferenta.
 *
 * Aktywne pozycje wypełniają ankietę w nowym postępowaniu; kupiec i tak może ją tam
 * zmienić. „Aktywne" ≠ „istnieje" — pytanie może zostać w bibliotece, ale nie wchodzić
 * domyślnie, gdy dotyczy tylko części zapytań.
 */

export function DeclarationLibraryPage() {
  const { user } = useAuthStore();
  const canEdit = canManageRfqScopes(user);
  usePageTitle("Domyślne oświadczenia");

  const { data: questions, isLoading } = useDefaultDeclarationQuestions();
  const save = useSaveDeclarationQuestion();
  const remove = useDeleteDeclarationQuestion();
  const [draft, setDraft] = useState("");

  if (isLoading) return <PageLoader />;
  const list = questions ?? [];

  const add = () => {
    if (!draft.trim()) return;
    save.mutate(
      { text: draft.trim(), is_active: true, sort_order: (list.length + 1) * 10 },
      { onSuccess: () => setDraft("") },
    );
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Domyślne oświadczenia oferenta
        </h1>
        <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          Pytania TAK/NIE, które nowe postępowanie dostaje automatycznie (polisa OC, brak
          zaległości, uprawnienia). Kupiec może je przy konkretnym zapytaniu dodać, usunąć
          lub zmienić — biblioteka to tylko punkt startu.
        </p>
      </div>

      <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] bg-white dark:bg-gray-800">
        {list.map((q) => (
          <div key={q.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            <GripVertical size={14} className="shrink-0 text-[var(--color-text-muted)]" />
            <span className="flex-1 text-[var(--color-text-primary)]">{q.text}</span>
            {canEdit ? (
              <label
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]"
                title="Czy pytanie wchodzi domyślnie do nowego postępowania"
              >
                <input
                  type="checkbox"
                  checked={q.is_active}
                  onChange={(e) => save.mutate({ id: q.id, is_active: e.target.checked })}
                />
                Domyślne
              </label>
            ) : (
              q.is_active && <Badge color="green">Domyślne</Badge>
            )}
            {canEdit && (
              <button
                onClick={() => {
                  if (confirm(`Usunąć oświadczenie „${q.text}”?`)) remove.mutate(q.id);
                }}
                className="text-[var(--color-text-muted)] hover:text-red-600"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        {list.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-[var(--color-text-muted)]">
            Biblioteka jest pusta.
          </p>
        )}
      </div>

      {canEdit && (
        <div className="mt-4 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Nowe oświadczenie, np. Posiadam aktualną polisę OC…"
            className="flex-1 border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
          />
          <Button onClick={add} disabled={save.isPending || !draft.trim()}>
            <Plus size={14} className="mr-1 inline" /> Dodaj
          </Button>
        </div>
      )}
    </div>
  );
}
